import {Stack, UnstyledButton} from '@mantine/core'
import {useMediaQuery} from '@mantine/hooks'
import {
    IconBackhoe,
    IconBriefcase,
    IconBuildingFactory2,
    IconCalendarStats,
    IconDashboard,
    IconFileDollar,
    IconFileInvoice,
    IconHome2,
    IconLogout,
    IconRoute,
    IconSettings,
    IconTruckDelivery,
    IconUserCog,
    IconUsers,
} from '@tabler/icons-react'
import {Link, type LinkOptions, linkOptions, useRouterState} from '@tanstack/react-router'

import {useAuthContext} from '../auth/auth-context'
import {isAdmin} from '../auth/is-admin'
import {useDispatchAttentionCount} from '../dispatch/use-dispatch'
import {NEW_WIZARD_SEARCH} from '../jobs/create-job-storage'
import {FeedbackNavButton} from '../sentry/feedback-nav-button'
import {AppBrandMark} from './app-brand-mark'
import classes from './app-navbar.module.css'

type NavItem = {
    icon: typeof IconDashboard
    label: string
    link: LinkOptions
    adminOnly?: boolean
    isActive: (pathname: string) => boolean
}

const navItems: NavItem[] = [
    {
        icon: IconDashboard,
        label: 'Dashboard',
        link: linkOptions({to: '/'}),
        isActive: (pathname) => pathname === '/',
    },
    {
        icon: IconRoute,
        label: 'Dispatch',
        link: linkOptions({to: '/dispatch'}),
        isActive: (pathname) => pathname.startsWith('/dispatch'),
    },
    {
        icon: IconFileDollar,
        label: 'Quotes',
        link: linkOptions({to: '/quotes'}),
        isActive: (pathname) => pathname.startsWith('/quotes'),
    },
    {
        icon: IconBuildingFactory2,
        label: 'Orders',
        link: linkOptions({to: '/projects'}),
        isActive: (pathname) =>
            pathname.startsWith('/projects') ||
            (pathname.startsWith('/jobs') && !pathname.startsWith('/jobs/create')),
    },
    {
        icon: IconCalendarStats,
        label: 'Active rentals',
        link: linkOptions({to: '/active-rentals'}),
        isActive: (pathname) => pathname.startsWith('/active-rentals'),
    },
    {
        icon: IconTruckDelivery,
        label: 'Create job',
        link: linkOptions({to: '/jobs/create', search: NEW_WIZARD_SEARCH}),
        isActive: (pathname) => pathname.startsWith('/jobs/create'),
    },
    {
        icon: IconFileInvoice,
        label: 'Invoices',
        link: linkOptions({to: '/invoices'}),
        isActive: (pathname) => pathname.startsWith('/invoices'),
    },
    {
        icon: IconBriefcase,
        label: 'Customers',
        link: linkOptions({to: '/customers'}),
        isActive: (pathname) => pathname.startsWith('/customers'),
    },
    {
        icon: IconUsers,
        label: 'Workers',
        link: linkOptions({to: '/workers'}),
        isActive: (pathname) => pathname.startsWith('/workers'),
    },
    {
        icon: IconBackhoe,
        label: 'Equipment',
        link: linkOptions({to: '/rental-assets'}),
        isActive: (pathname) => pathname.startsWith('/rental-assets'),
    },
    {
        icon: IconUserCog,
        label: 'Users',
        link: linkOptions({to: '/users'}),
        adminOnly: true,
        isActive: (pathname) => pathname.startsWith('/users'),
    },
    {
        icon: IconSettings,
        label: 'Settings',
        link: linkOptions({to: '/settings'}),
        adminOnly: true,
        isActive: (pathname) => pathname.startsWith('/settings'),
    },
]

type NavbarLinkProps = {
    icon: typeof IconHome2
    label: string
    active?: boolean
    link?: LinkOptions
    onClick?: () => void
    onNavigate?: () => void
    badgeCount?: number
}

function NavbarLink({
    icon: Icon,
    label,
    active,
    link,
    onClick,
    onNavigate,
    badgeCount,
}: NavbarLinkProps) {
    const showBadge = badgeCount != null && badgeCount > 0
    const ariaLabel = showBadge ? `${label} (${badgeCount} need attention)` : label

    const handleClick = () => {
        onClick?.()
        onNavigate?.()
    }

    return (
        <UnstyledButton
            className={classes['link']}
            fz="sm"
            data-active={active || undefined}
            aria-label={ariaLabel}
            {...(link != null
                ? {renderRoot: (props) => <Link {...props} {...link} />, onClick: onNavigate}
                : {onClick: handleClick})}
        >
            <Icon className={classes['linkIcon']} size={18} stroke={1.5} />
            <span className={classes['linkLabel']}>{label}</span>
            {showBadge && <span className={classes['linkBadge']}>{badgeCount}</span>}
        </UnstyledButton>
    )
}

type AppNavbarProps = {
    onNavigate?: () => void
}

export function AppNavbar({onNavigate}: AppNavbarProps) {
    const pathname = useRouterState({select: (state) => state.location.pathname})
    const {user, isLoading, logout} = useAuthContext()
    const isDesktopNav = useMediaQuery('(min-width: 48em)')
    const {data: attentionCount} = useDispatchAttentionCount({
        enabled: !isLoading && user != null,
    })

    const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAdmin(user))

    const links = visibleNavItems.map((item) => (
        <NavbarLink
            key={item.label}
            icon={item.icon}
            label={item.label}
            link={item.link}
            active={item.isActive(pathname)}
            onNavigate={onNavigate}
            badgeCount={item.label === 'Dispatch' ? attentionCount?.count : undefined}
        />
    ))

    return (
        <nav className={classes['navbar']}>
            {isDesktopNav && (
                <div className={classes['header']}>
                    <AppBrandMark />
                </div>
            )}

            <div className={classes['navbarMain']} data-testid="navbar-main">
                <Stack justify="center" className={classes['navLinks']}>
                    {links}
                </Stack>
            </div>

            <div className={classes['footer']}>
                <Stack justify="center" gap={0}>
                    <FeedbackNavButton onNavigate={onNavigate} />
                    <NavbarLink
                        icon={IconLogout}
                        label="Logout"
                        onClick={logout}
                        onNavigate={onNavigate}
                    />
                </Stack>
            </div>
        </nav>
    )
}
